/// <reference lib="dom" />
export {}; // module scope
import { DN_INNER, flowM3h, velocity, recommendDn, VELOCITY_MAX } from "../shared/flowData.js";

type Product = {
  variantId: number; sku: string; name: string; type: string; diameter: number;
  priceCents: number | null; priceText: string; imageUrl: string | null; href: string;
};
type Data = { products: Product[]; lang: string; strings: Record<string, string> };

const dataEl = document.getElementById("flowsizing-data");
const results = document.getElementById("fs-results");
if (dataEl?.textContent && results) {
  try {
    boot(JSON.parse(dataEl.textContent) as Data, results);
  } catch {
    /* malformed */
  }
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function bumpCount(n: number) {
  document.querySelectorAll("[data-cart-count]").forEach((el) => {
    const c = parseInt(el.textContent || "0", 10) || 0;
    el.textContent = String(c + n);
    (el as HTMLElement).classList.remove("hidden");
  });
}

function boot(data: Data, results: HTMLElement) {
  const s = data.strings;
  const t = (k: string) => s[k] ?? k;
  const locale = data.lang === "de" ? "de-DE" : "en-US";
  const nf2 = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  const nf0 = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  const ids = ["fs-power", "fs-dt", "fs-length"];
  const els = Object.fromEntries(ids.map((id) => [id, document.getElementById(id) as HTMLInputElement])) as Record<string, HTMLInputElement>;
  for (const id of ids) els[id]?.addEventListener("input", render);
  results.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest("[data-add-cart]") as HTMLButtonElement | null;
    if (!btn || btn.disabled) return;
    void addToCart(Number(btn.dataset.vid), Number(btn.dataset.len), btn);
  });
  render();

  function render() {
    const kW = Number(els["fs-power"]?.value) || 0;
    const dt = Number(els["fs-dt"]?.value) || 0;
    const length = Number(els["fs-length"]?.value) || 0;
    const flow = flowM3h(kW, dt);
    const rec = recommendDn(flow);
    const vRec = velocity(flow, rec.innerMm);

    const rows = DN_INNER.map((row) => {
      const v = velocity(flow, row.innerMm);
      const ok = v <= VELOCITY_MAX;
      const isRec = row.dn === rec.dn;
      return `<tr class="${isRec ? "bg-brand-50 font-semibold" : ""}">
        <td>DN${row.dn}</td>
        <td class="text-right">${nf2.format(v)} m/s</td>
        <td class="text-right">${ok ? `<span class="text-emerald-600">✓</span>` : `<span class="text-red-500">↑</span>`}</td>
      </tr>`;
    }).join("");

    const matched = data.products.filter((p) => Math.round(p.diameter) === rec.dn);
    const hasLen = length > 0;
    const cards = matched
      .map((p) => {
        const addBtn = hasLen && p.priceCents != null
          ? `<button type="button" data-add-cart data-vid="${p.variantId}" data-len="${Math.round(length)}" class="btn-primary mt-2 inline-flex items-center gap-1 text-xs"><i class="fa-solid fa-cart-plus"></i> ${esc(t("prod_add"))} ${nf0.format(length)} m</button>`
          : "";
        return `<div class="card flex gap-3 p-3">
          ${p.imageUrl ? `<img src="${esc(p.imageUrl)}" alt="${esc(p.sku)}" class="h-16 w-16 rounded object-contain"/>` : ""}
          <div class="min-w-0 flex-1">
            <a href="${esc(p.href)}" class="block font-medium text-slate-900">${esc(p.name)}</a>
            <div class="text-xs text-slate-400">${esc(p.sku)} · ${esc(p.priceText)}/m</div>
            ${addBtn}
          </div>
        </div>`;
      })
      .join("");

    results.innerHTML = `
      <div class="card p-4">
        <div class="grid grid-cols-3 gap-3 text-center">
          <div><div class="text-2xl font-semibold text-slate-900">${nf2.format(flow)}</div><div class="text-xs text-slate-500">${esc(t("flow"))}</div></div>
          <div><div class="text-2xl font-semibold text-brand-700">DN${rec.dn}</div><div class="text-xs text-slate-500">${esc(t("recommended"))}</div></div>
          <div><div class="text-2xl font-semibold text-slate-900">${nf2.format(vRec)}</div><div class="text-xs text-slate-500">${esc(t("velocity"))}</div></div>
        </div>
        <div class="mt-3 overflow-x-auto">
          <table class="table text-sm"><thead><tr><th>DN</th><th class="text-right">${esc(t("velocity"))}</th><th class="text-right">${esc(t("ok"))}</th></tr></thead><tbody>${rows}</tbody></table>
        </div>
        <p class="mt-2 text-[11px] text-slate-400">${esc(t("estimate_note"))}</p>
      </div>
      ${matched.length ? `<h2 class="mt-4 text-base font-semibold text-slate-700">${esc(t("matched"))}</h2><div class="mt-2 grid gap-3 sm:grid-cols-2">${cards}</div>` : ""}`;
  }

  async function addToCart(variantId: number, unitAmount: number, btn: HTMLButtonElement) {
    if (!variantId || !(unitAmount > 0)) return;
    btn.disabled = true;
    try {
      const r = await fetch("/cart/add", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded", Accept: "application/json" },
        body: new URLSearchParams({ variantId: String(variantId), qty: "1", unitAmount: String(unitAmount) }),
      });
      if (r.ok) {
        bumpCount(1);
        btn.innerHTML = `<i class="fa-solid fa-check"></i> ${esc(t("prod_added"))}`;
      }
    } catch {
      /* ignore */
    } finally {
      setTimeout(() => { btn.disabled = false; }, 1500);
    }
  }
}
